import { useEffect, useRef, useState, type FormEvent } from 'react';

/**
 * Lightweight bug-report / feedback modal. Posts to /api/feedback with
 * the user's note plus auto-captured page/viewport/user-agent so triage
 * has reproduction context without the user having to remember any of
 * it.
 *
 * Visible from the app footer; deliberately not behind auth so a
 * crash-fallback render can still expose it.
 */
export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'bug' | 'idea' | 'moderation' | 'other'>('bug');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body,
          kind,
          page: window.location.pathname + window.location.search,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `HTTP ${res.status}`);
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <>
            <h2>Thanks — got it.</h2>
            <p className="muted">
              We read every report. If you included details about what broke, we'll get back to you
              at the email tied to your account.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <h2>Report a bug or share an idea</h2>
            <p className="muted">
              Tell us what happened — we'll capture the page you're on and your screen size
              automatically.
            </p>
            <label className="field-label">
              Kind
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
                disabled={submitting}
              >
                <option value="bug">Something is broken</option>
                <option value="idea">I have an idea</option>
                <option value="moderation">A moderation issue</option>
                <option value="other">Something else</option>
              </select>
            </label>
            <label className="field-label">
              What's going on?
              <textarea
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                maxLength={4000}
                required
                minLength={3}
                disabled={submitting}
                placeholder="e.g. the send button disappears when I open the keyboard"
              />
            </label>
            {error && <p className="error">{error}</p>}
            <div className="modal-actions">
              <button type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" disabled={submitting || body.trim().length < 3}>
                {submitting ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
